import { HttpClient, HttpEvent, HttpEventType } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, catchError, map, of } from "rxjs";
import { environment } from "src/environments/environment";
import {
  AssetCategoryViewModel,
  DemoAssetCarouselModel,
  DemoAssetFormViewModel,
  DemoAssetMediaModel,
  DemoAssetNameViewModel,
  DemoAssetPopupViewModel,
  DemoAssetTemplateViewModel,
  DemoAssetViewModel
} from "../core/model/ContentManagement";
import { BrdLoggerService } from "@board/cloud-library/components/services";
import { MetadataService } from "./metadata.service";
import { brdCatchError } from "../shared/message-utils";
import { Slide } from "@board/cloud-library/components/carousel";

@Injectable({
  providedIn: "root"
})
export class DemoAssetService {
  controllerName = "DemoAsset";
  private apiUrl: string = `${environment.API}/${this.controllerName}`;

  private assetsSubject = new BehaviorSubject<DemoAssetViewModel[]>([]);
  assets$ = this.assetsSubject.asObservable();

  private assetNamesSubject = new BehaviorSubject<DemoAssetNameViewModel[]>([]);
  assetNames$ = this.assetNamesSubject.asObservable();

  private assetCategoriesSubject = new BehaviorSubject<AssetCategoryViewModel[]>([]);
  assetCategories$ = this.assetCategoriesSubject.asObservable();

  constructor(
    private httpClient: HttpClient,
    private loggerService: BrdLoggerService,
    private metadataService: MetadataService
  ) {}

  getAssets(includeSuggestedDemos = false, includeDraft = false, includeDefault = false): Observable<DemoAssetViewModel[]> {
    return this.httpClient
      .get<
        DemoAssetViewModel[]
      >(`${this.apiUrl}?includeSuggestedDemos=${includeSuggestedDemos}&includeDraft=${includeDraft}&includeDefault=${includeDefault}`)
      .pipe(
        map(res => {
          this.assetsSubject.next(res);
          return res;
        })
      );
  }

  addOrUpdateAsset(
    assetModel: DemoAssetFormViewModel,
    demoSource?: File,
    defaultImage?: File,
    hoveredImage?: File,
    carousel?: Slide[],
    agentsConfig?: File[] | null
  ): Observable<HttpEvent<DemoAssetViewModel>> {
    const formData = new FormData();
    formData.append("demoAssetModelRaw", JSON.stringify(assetModel));
    if (demoSource) formData.append("demoSource", demoSource, demoSource.name);
    if (defaultImage) formData.append("cardDefaultImage", defaultImage, defaultImage.name);
    if (hoveredImage) formData.append("cardHoveredImage", hoveredImage, hoveredImage.name);
    if (carousel)
      carousel.forEach(s => {
        formData.append("carousel", this.getBlobFromSource(s.source), s.fileName);
      });
    if (agentsConfig) agentsConfig.forEach(x => formData.append("agentsConfig", x, x.name));

    return this.httpClient
      .post<DemoAssetViewModel>(`${this.apiUrl}`, formData, {
        reportProgress: true,
        observe: "events"
      })
      .pipe(
        map((res: HttpEvent<DemoAssetViewModel>) => {
          if (res.type == HttpEventType.Response) {
            if (res.status >= 400) {
              throw new Error(res.statusText);
            } else if (res.status >= 200 && res.status < 300) {
              const currentAssets = [...this.assetsSubject.value];
              const assetIndex = currentAssets.findIndex(x => x.id === res.body?.id) ?? -1;
              if (assetIndex > -1) currentAssets[assetIndex] = res.body!;
              else currentAssets.push(res.body!);

              this.assetsSubject.next(currentAssets);

              this.metadataService.updateIndustries(res.body!.industries ?? []);
            }
          }
          return res;
        }),
        catchError((message: unknown) => {
          this.loggerService.error(message, "");
          return of();
        })
      );
  }

  updateOrders(ids: number[]): Observable<void> {
    return this.httpClient.put<void>(`${this.apiUrl}/orders`, ids);
  }

  deleteAsset(id: number): Observable<DemoAssetViewModel> {
    return this.httpClient.delete<DemoAssetViewModel>(`${this.apiUrl}/${id}`).pipe(
      map(res => {
        const currentAssets = [...this.assetsSubject.value];
        const assetIndex = currentAssets.findIndex(x => x.id === res.id);

        if (assetIndex > -1) {
          currentAssets.splice(assetIndex, 1);
          this.assetsSubject.next(currentAssets);
        }

        return res;
      })
    );
  }

  getAsset(id: number): Observable<DemoAssetFormViewModel> {
    return this.httpClient.get<DemoAssetFormViewModel>(`${this.apiUrl}/${id}`);
  }

  getAssetPopup(id: number): Observable<DemoAssetPopupViewModel> {
    return this.httpClient.get<DemoAssetPopupViewModel>(`${this.apiUrl}/${id}/popup`);
  }

  getAssetMedia(assetId: number, superUser: boolean = false): Observable<DemoAssetMediaModel> {
    const url = superUser ? "superusermedia" : "media";
    return this.httpClient.get<DemoAssetMediaModel>(`${this.apiUrl}/${assetId}/${url}`);
  }

  getAssetCarousel(assetId: number, superUser: boolean = false): Observable<DemoAssetCarouselModel> {
    const url = superUser ? "superusercarousel" : "carousel";
    return this.httpClient.get<DemoAssetCarouselModel>(`${this.apiUrl}/${assetId}/${url}`);
  }

  getAssetTemplate(assetId: number): Observable<DemoAssetTemplateViewModel[]> {
    return this.httpClient.get<DemoAssetTemplateViewModel[]>(`${this.apiUrl}/${assetId}/template`);
  }

  downloadZip(assetId: number): Observable<string> {
    return this.httpClient.get<{ sasUri: string }>(`${this.apiUrl}/${assetId}/zip`).pipe(
      map(res => {
        if (res.sasUri) {
          const a = document.createElement("a");
          a.href = res.sasUri;
          a.click();
        } else {
          this.loggerService.error("Error on dowloading zip!", "");
        }

        return res.sasUri;
      }),
      catchError((message: unknown) => {
        this.loggerService.error(message, "");
        return of();
      })
    );
  }

  getAssetNames(): Observable<DemoAssetNameViewModel[]> {
    return this.httpClient.get<DemoAssetNameViewModel[]>(`${this.apiUrl}/names`).pipe(
      map(res => {
        this.assetNamesSubject.next(res);
        return res;
      })
    );
  }

  getCategories(): Observable<AssetCategoryViewModel[]> {
    return this.httpClient.get<AssetCategoryViewModel[]>(`${this.apiUrl}/categories`).pipe(
      map(res => {
        this.setCategories(res);
        return res;
      })
    );
  }

  setCategories(value: AssetCategoryViewModel[]): void {
    this.assetCategoriesSubject.next(value);
  }

  deleteCategory(id: number): Observable<AssetCategoryViewModel> {
    return this.httpClient.delete<AssetCategoryViewModel>(`${this.apiUrl}/categories/${id}`).pipe(
      brdCatchError(),
      map(res => {
        let currentCategories = [...this.assetCategoriesSubject.value];
        currentCategories = currentCategories.filter(x => x.id !== id);

        this.assetCategoriesSubject.next(currentCategories);
        return res;
      })
    );
  }

  addCategory(category: AssetCategoryViewModel): Observable<AssetCategoryViewModel> {
    return this.httpClient.post<AssetCategoryViewModel>(`${this.apiUrl}/categories`, category).pipe(
      brdCatchError(),
      map(res => {
        this.assetCategoriesSubject.next([...this.assetCategoriesSubject.value, res]);
        return res;
      })
    );
  }

  updateCategory(id: number, category: AssetCategoryViewModel): Observable<AssetCategoryViewModel> {
    return this.httpClient.put<AssetCategoryViewModel>(`${this.apiUrl}/categories/${id}`, category).pipe(
      brdCatchError(),
      map(res => {
        const currentCategories = [...this.assetCategoriesSubject.value];
        const categoryIndex = currentCategories.findIndex(x => x.id === res.id);
        currentCategories[categoryIndex] = res;

        this.assetCategoriesSubject.next(currentCategories);
        return res;
      })
    );
  }

  private getBlobFromSource(source: string): Blob {
    const re = /data:(?<mime>\S+);base64,(?<source>.+)/g;
    const results = [...re.exec(source)];

    const binary = atob(results[2]);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);

    return new Blob([bytes], { type: results[1] });
  }
}
